import React, { useEffect, useState } from "react";
import { FileText, DownloadSimple, MagnifyingGlass, Books, Van } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { PageContainer, EmptyState } from "@/components/customer/PageContainer";
import { useCustomer } from "@/context/CustomerContext";
import { api, API } from "@/lib/api";

const TYPE_LABELS = {
  manual: "Manual",
  wiring_diagram: "Wiring Diagram",
  technical: "Technical",
  troubleshooting: "Troubleshooting",
  spec: "Specification",
  service_bulletin: "Service Bulletin",
  warranty: "Warranty",
  training: "Training",
  other: "Document",
};

export function DocumentLibrary({ overline, title, subtitle, docType, excludeType, emptyHint }) {
  const { caravan } = useCustomer();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = {};
    if (docType) params.doc_type = docType;
    if (excludeType) params.exclude_type = excludeType;
    if (caravan?.chassis_number) params.chassis = caravan.chassis_number;
    setLoading(true);
    api
      .get("/documents", { params })
      .then(({ data }) => setDocs(data))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [docType, excludeType, caravan]);

  const filtered = docs.filter((d) => {
    if (!q) return true;
    const hay = `${d.title} ${d.category} ${d.equipment} ${(d.applies_to_models || []).join(" ")}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <PageContainer
      overline={overline}
      title={title}
      subtitle={subtitle}
      actions={
        <div className="flex items-center gap-2 bg-[#141414] border border-neutral-800 rounded-full px-4 py-2 w-64 focus-within:border-[#73bf44] transition-colors">
          <MagnifyingGlass size={16} className="text-neutral-500" />
          <input
            data-testid="document-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search..."
            className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600"
          />
        </div>
      }
    >
      {caravan?.chassis_number && (
        <div className="mb-5 flex flex-wrap items-center gap-2 text-xs text-neutral-400 bg-[#141414] border border-neutral-800 rounded-lg px-4 py-2.5" data-testid="manuals-filter-note">
          <Van size={15} className="text-[#73bf44]" weight="duotone" />
          Showing documents relevant to your{" "}
          <span className="text-white font-medium">{caravan.model} {caravan.year}</span>
          <span className="text-neutral-600">({caravan.chassis_number})</span>
        </div>
      )}
      {loading ? (
        <div className="text-neutral-600">Loading...</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Books} title="No documents available yet" hint={emptyHint} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d, i) => (
            <motion.a
              key={d.id}
              href={`${API}/documents/${d.id}/file`}
              target="_blank"
              rel="noreferrer"
              data-testid="document-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="group bg-[#171717] border border-neutral-700 hover:border-[#73bf44] rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 flex flex-col"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-lg bg-[#73bf44]/10 flex items-center justify-center">
                  <FileText size={22} className="text-[#73bf44]" weight="duotone" />
                </div>
                <DownloadSimple size={18} className="text-neutral-500 group-hover:text-[#73bf44] transition-colors" />
              </div>
              <div className="font-heading font-semibold text-white leading-tight mb-1">{d.title}</div>
              {d.category && <div className="text-xs text-neutral-400 mb-3">{d.category}</div>}
              <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
                <span className="text-[0.65rem] px-2 py-0.5 rounded-full bg-black/50 border border-neutral-600 text-neutral-300">
                  {TYPE_LABELS[d.doc_type] || "Document"}
                </span>
                {(d.applies_to_models || []).slice(0, 2).map((m) => (
                  <span key={m} className="text-[0.65rem] px-2 py-0.5 rounded-full bg-black/50 border border-neutral-600 text-neutral-300">
                    {m}
                  </span>
                ))}
              </div>
            </motion.a>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
